import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Download,
  Edit3,
  MapPin,
  Package,
  Search,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';

import { generateExcelFile } from '../../../utils/provinceExcelParser';
import { validateProvinceDelivery } from '../../../utils/provinceDeliveryValidator';


function formatReservationDate(value) {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-PE', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function ProvinceDeliveryCard({ delivery, onEdit, onDelete }) {
  const { data, errors, warnings, isValid } = delivery;
  const items = [1, 2, 3, 4, 5]
    .map(index => ({ sku: data[`SKU ${index}`], quantity: data[`Cantidad ${index}`] }))
    .filter(item => item.sku);

  return (
    <article className={`rounded-xl border-2 p-4 ${isValid ? (warnings.length ? 'border-brand-gold/50 bg-brand-gold/5' : 'border-brand-green/30 bg-brand-green/5') : 'border-red-200 bg-red-50/60'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-montserrat text-xs font-bold text-slate-500">Fila {delivery.rowNumber}</span>
            <span className={`rounded-full px-2 py-0.5 font-montserrat text-xs font-semibold ${isValid ? 'bg-brand-green/15 text-brand-green' : 'bg-red-100 text-red-700'}`}>
              {isValid ? (warnings.length ? 'Válida con avisos' : 'Válida') : 'Con errores'}
            </span>
          </div>
          <h3 className="mt-2 truncate font-montserrat font-bold text-slate-900">{data['Nombre y Apellido'] || 'Sin nombre'}</h3>
          <p className="font-montserrat text-sm text-slate-600">{data.WhatsApp || 'Sin WhatsApp'} · {data.DNI ? `DNI ${data.DNI}` : 'Sin DNI'}</p>
        </div>
        <div className="flex flex-shrink-0 gap-1">
          <button type="button" onClick={() => onEdit(delivery)} className="rounded-lg p-2 text-brand-primary hover:bg-brand-primary/10" aria-label={`Editar fila ${delivery.rowNumber}`}>
            <Edit3 className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onDelete(delivery.rowNumber)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label={`Eliminar fila ${delivery.rowNumber}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 font-montserrat text-sm text-slate-700 sm:grid-cols-2">
        <span className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />{data.Distrito}, {data.Provincia}</span>
        <span className="flex items-start gap-2"><Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-primary" />{data['Direccion de Agencia']}</span>
        <span className="sm:col-span-2">Dirección: {data.Direccion}</span>
        <span className="sm:col-span-2">Productos: {items.map(item => `${item.quantity || 1}x ${item.sku}`).join(', ') || '-'}</span>
        <span>Cobrar: S/ {Number(data.Cobrar || 0).toFixed(2)}</span>
        <span>Adelanto: S/ {Number(data.Adelanto || 0).toFixed(2)}</span>
      </div>

      {errors.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-lg bg-red-100/70 p-3">
          {errors.map((error, index) => <li key={`${error.field}-${index}`} className="font-montserrat text-xs text-red-700">• {error.message}</li>)}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-lg bg-brand-gold/10 p-3">
          {warnings.map((warning, index) => <li key={`${warning.field}-${index}`} className="font-montserrat text-xs text-amber-800">• {warning.message}</li>)}
        </ul>
      )}
    </article>
  );
}

function ProvinceDeliveryEditModal({ delivery, provinces, userProducts, onClose, onSave }) {
  const [formData, setFormData] = useState({ ...delivery.data });
  const [localErrors, setLocalErrors] = useState([]);
  const productListId = `province-products-${delivery.rowNumber}`;

  function change(field, value) {
    setFormData(previous => ({ ...previous, [field]: value }));
    setLocalErrors([]);
  }

  function save() {
    const validated = validateProvinceDelivery(formData, delivery.rowNumber, provinces, userProducts);
    if (!validated.isValid) {
      setLocalErrors(validated.errors);
      return;
    }
    onSave(validated);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <h3 className="font-montserrat text-lg font-bold text-brand-primary">Editar fila {delivery.rowNumber}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </header>

        <div className="space-y-4 p-6">
          {localErrors.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              {localErrors.map((error, index) => <p key={`${error.field}-${index}`} className="font-montserrat text-sm text-red-700">• {error.message}</p>)}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre y Apellido *" value={formData['Nombre y Apellido']} onChange={value => change('Nombre y Apellido', value)} />
            <Field label="WhatsApp *" value={formData.WhatsApp} onChange={value => change('WhatsApp', value.replace(/\D/g, '').slice(0, 9))} />
            <Field label="DNI" value={formData.DNI} onChange={value => change('DNI', value.replace(/\D/g, '').slice(0, 8))} />
            <label className="font-montserrat text-sm font-semibold text-slate-700">
              Provincia *
              <select value={formData.Provincia || ''} onChange={event => change('Provincia', event.target.value)} className="mt-2 w-full rounded-lg border-2 border-slate-300 px-3 py-2 font-normal focus:border-brand-gold focus:outline-none">
                <option value="">Seleccionar...</option>
                {provinces.map(province => <option key={province} value={province}>{province}</option>)}
              </select>
            </label>
            <Field label="Distrito *" value={formData.Distrito} onChange={value => change('Distrito', value)} />
            <Field label="Dirección *" value={formData.Direccion} onChange={value => change('Direccion', value)} />
            <div className="sm:col-span-2"><Field label="Dirección de Agencia *" value={formData['Direccion de Agencia']} onChange={value => change('Direccion de Agencia', value)} /></div>
          </div>

          <datalist id={productListId}>
            {userProducts.map(product => <option key={product.id || product.sku} value={product.sku}>{product.title}</option>)}
          </datalist>
          <div>
            <p className="mb-2 font-montserrat text-sm font-semibold text-slate-700">Productos *</p>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(index => (
                <div key={index} className="grid grid-cols-[1fr_110px] gap-2">
                  <input list={productListId} value={formData[`SKU ${index}`] || ''} onChange={event => change(`SKU ${index}`, event.target.value)} placeholder={`SKU ${index}`} className="rounded-lg border-2 border-slate-300 px-3 py-2 font-montserrat text-sm focus:border-brand-primary focus:outline-none" />
                  <input type="number" min="1" value={formData[`Cantidad ${index}`] || (index === 1 ? '1' : '')} onChange={event => change(`Cantidad ${index}`, event.target.value)} placeholder="Cantidad" className="rounded-lg border-2 border-slate-300 px-3 py-2 font-montserrat text-sm focus:border-brand-primary focus:outline-none" />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field type="number" label="Cobrar (S/)" value={formData.Cobrar} onChange={value => change('Cobrar', value)} />
            <Field type="number" label="Adelanto opcional (S/)" value={formData.Adelanto} onChange={value => change('Adelanto', value)} />
            <div className="sm:col-span-2"><Field label="Notas" value={formData.Notas} onChange={value => change('Notas', value)} /></div>
          </div>
        </div>

        <footer className="sticky bottom-0 flex gap-3 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl border-2 border-slate-300 px-4 py-3 font-montserrat font-semibold text-slate-700">Cancelar</button>
          <button type="button" onClick={save} className="flex-1 rounded-xl bg-brand-primary px-4 py-3 font-montserrat font-semibold text-white">Guardar cambios</button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <label className="block font-montserrat text-sm font-semibold text-slate-700">
      {label}
      <input type={type} min={type === 'number' ? '0' : undefined} step={type === 'number' ? '0.01' : undefined} value={value || ''} onChange={event => onChange(event.target.value)} className="mt-2 w-full rounded-lg border-2 border-slate-300 px-3 py-2 font-normal focus:border-brand-gold focus:outline-none" />
    </label>
  );
}

function ProvinceDeliveriesReview({ deliveries, selectedDate, provinces, userProducts, onUpdate, onDelete, onProceed, onChangeDate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const validCount = deliveries.filter(delivery => delivery.isValid).length;
  const errorCount = deliveries.length - validCount;
  const warningCount = deliveries.filter(delivery => delivery.isValid && delivery.hasWarnings).length;
  const canProceed = deliveries.length > 0 && errorCount === 0;

  const filtered = useMemo(() => deliveries.filter((delivery) => {
    if (filter === 'valid' && !delivery.isValid) return false;
    if (filter === 'warnings' && !delivery.hasWarnings) return false;
    if (filter === 'errors' && delivery.isValid) return false;
    if (!searchTerm.trim()) return true;
    const query = searchTerm.toLowerCase();
    const data = delivery.data;
    return data['Nombre y Apellido']?.toLowerCase().includes(query)
      || data.WhatsApp?.includes(query)
      || data.Provincia?.toLowerCase().includes(query)
      || data.Distrito?.toLowerCase().includes(query)
      || String(delivery.rowNumber).includes(query);
  }), [deliveries, filter, searchTerm]);

  function downloadErrors() {
    const rows = deliveries.filter(delivery => !delivery.isValid).map(delivery => ({
      Fila: delivery.rowNumber,
      ...delivery.data,
      Errores: delivery.errors.map(error => error.message).join('; '),
    }));
    const blob = generateExcelFile(rows, 'errores_provincia.xlsx');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Errores_Carga_Masiva_Provincia.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="rounded-2xl border-2 border-slate-200 bg-white p-6 sm:p-8">
      <h2 className="font-montserrat text-xl font-extrabold text-brand-primary sm:text-2xl">Paso 3: Revisa el lote</h2>
      <p className="mt-1 font-montserrat text-sm text-slate-600">Corrige todos los errores antes de crear las entregas.</p>

      <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-brand-gold/30 bg-brand-gold/10 p-4">
        <span className="flex items-center gap-2 font-montserrat text-sm font-semibold text-slate-700"><Calendar className="h-5 w-5 text-brand-gold" />Reserva: {formatReservationDate(selectedDate)}</span>
        <button type="button" onClick={onChangeDate} className="font-montserrat text-sm font-semibold text-brand-primary hover:underline">Cambiar</button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Summary label="Total" value={deliveries.length} />
        <Summary label="Válidas" value={validCount} tone="green" icon={<CheckCircle className="h-4 w-4" />} />
        <Summary label="Avisos" value={warningCount} tone="gold" icon={<AlertTriangle className="h-4 w-4" />} />
        <Summary label="Errores" value={errorCount} tone="red" icon={<XCircle className="h-4 w-4" />} />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <label className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder="Buscar por nombre, teléfono, destino o fila" className="w-full rounded-lg border-2 border-slate-300 py-2 pl-9 pr-3 font-montserrat text-sm focus:border-brand-primary focus:outline-none" />
        </label>
        <select value={filter} onChange={event => setFilter(event.target.value)} className="rounded-lg border-2 border-slate-300 px-3 py-2 font-montserrat text-sm focus:border-brand-primary focus:outline-none">
          <option value="all">Todas</option><option value="valid">Válidas</option><option value="warnings">Con avisos</option><option value="errors">Con errores</option>
        </select>
      </div>

      <div className="mt-4 max-h-[560px] space-y-3 overflow-y-auto pr-1">
        {filtered.map(delivery => <ProvinceDeliveryCard key={delivery.rowNumber} delivery={delivery} onEdit={setEditing} onDelete={onDelete} />)}
        {filtered.length === 0 && <p className="py-10 text-center font-montserrat text-sm text-slate-500">No hay filas para este filtro.</p>}
      </div>

      {errorCount > 0 && <p className="mt-5 rounded-xl border-l-4 border-red-500 bg-red-50 p-4 font-montserrat text-sm text-red-700">La operación es atómica: debes corregir o eliminar las {errorCount} fila{errorCount !== 1 ? 's' : ''} con errores.</p>}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        {errorCount > 0 && <button type="button" onClick={downloadErrors} className="flex items-center justify-center gap-2 rounded-xl border-2 border-red-300 px-4 py-3 font-montserrat font-semibold text-red-700"><Download className="h-5 w-5" />Descargar errores</button>}
        <button type="button" onClick={onProceed} disabled={!canProceed} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-green px-4 py-3 font-montserrat font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"><CheckCircle className="h-5 w-5" />Crear {validCount} entrega{validCount !== 1 ? 's' : ''}</button>
      </div>

      {editing && <ProvinceDeliveryEditModal delivery={editing} provinces={provinces} userProducts={userProducts} onClose={() => setEditing(null)} onSave={onUpdate} />}
    </section>
  );
}

function Summary({ label, value, tone = 'slate', icon }) {
  const classes = { slate: 'border-slate-200 bg-slate-50 text-slate-900', green: 'border-brand-green/30 bg-brand-green/10 text-brand-green', gold: 'border-brand-gold/30 bg-brand-gold/10 text-amber-700', red: 'border-red-200 bg-red-50 text-red-700' };
  return <div className={`rounded-xl border p-4 ${classes[tone]}`}><p className="font-montserrat text-xs text-slate-600">{label}</p><p className="mt-1 flex items-center gap-2 font-montserrat text-2xl font-bold">{icon}{value}</p></div>;
}

export default ProvinceDeliveriesReview;
